import axios from 'axios';

import { apiConfig } from '@constants';
import { helpers } from '@helpers';
import { navigationService } from '@services';
import { UserSettings, VideoData } from '@types';

import { getSegmentHeaders } from './getSegmentHeaders';
import { segmentEventTypes as eventType } from './segmentEventTypes';

import Manifest from '../../../manifest.json';
import { getDeviceVolume } from '../../helpers/getDeviceVolume';
import { getDeviceKey } from '../../helpers/KeyUtils';

type Events = typeof eventType[keyof typeof eventType];
type Arguments = {
  weatherForecast?: { featureId: string; mwSegmentId: number; weatherForecastCount: number };
  manageLocations?: { nextLocation?: string; searchTerm?: string };
  apiErrorData?: { source: string; payload: unknown; responseCode: number; response: unknown };
  weatherMap?: { featureId?: string; mwSegmentId?: string; mapView?: unknown; weatherForecastCount?: number };
  videoErrorData?: { source: string; exception: string; responseCode: string; response: string };
  liveRampData?: {
    response: string;
    api_call: string;
    refresh: string;
    envelope: string;
  };
};

const getDataForEvent = (event: Events, userSettings: UserSettings & VideoData, arguments_: Arguments) => {
  const {
    _id,
    episode,
    schedule,
    isLive,
    isANF,
    anf,
    title,
    email,
    seasonID,
    subscriptionStatus,
    priorSubscriptionStatus,
    locations,
    isCableSubscriber,
    pricePlan,
    seriestitle,
  } = userSettings;
  const { weatherForecast, manageLocations, apiErrorData, weatherMap, videoErrorData, liveRampData } = arguments_;

  const version = Manifest?.app_version;
  const build = Manifest?.build_number;
  const deviceId = getDeviceKey(); // Send deviceId for cable users for now

  const watchLocation = locations.find(location => location.isWatching);
  const defaultLocation = locations.find(location => location.isDefault);

  const watchCity = watchLocation?.city;
  const watchState = watchLocation?.state;
  const channelId = `${watchState?.toLowerCase()}${watchCity?.replace(/\s/g, '')}`;
  const currentLocation = `${defaultLocation?.state?.toLowerCase()}${defaultLocation?.city?.replace(/\s/g, '')}`;

  const timestamp = Date.now();
  const userId = isCableSubscriber ? deviceId : email;

  // Empty string for TVE users, interval units: monthly, annual
  const subscriptiontype = isCableSubscriber ? '' : helpers.getSubscriptionType(pricePlan);

  const adobeClientId = apiConfig.AUTH0_CLIENT_ID;
  const sessionId = isCableSubscriber ? `${adobeClientId}${timestamp}` : `${userId}${timestamp}`;

  const globalEventProperties = {
    version,
    build,
    subscriptionstate: isCableSubscriber ? 'cable' : subscriptionStatus,
    priorstate: priorSubscriptionStatus,
  };

  switch (event) {
    case eventType.LOGIN:
    case eventType.APP_INSTALLED:
      return {
        event,
        userId,
        properties: {
          ...globalEventProperties,
        },
      };
    case eventType.LOGOUT:
      return {
        event,
        userId,
        properties: {
          ...globalEventProperties,
          logout: 1,
        },
      };
    case eventType.LOCATION_WATCHING:
      return {
        event,
        userId,
        properties: {
          ...globalEventProperties,
          channel_id: channelId,
        },
      };
    case eventType.CITY_OVERVIEW:
      return {
        event,
        userId,
        properties: {
          ...globalEventProperties,
          videoId: `${channelId}_my_weather_segment_city_overview`,
          MW_segment_id: 1,
          Sequence_count: 0, // TODO: will eventually increment for every new thing opened when more tracking is added
        },
      };

    case eventType.VIDEO_HEARTBEAT:
    case eventType.VIDEO_PLAYBACK_STARTED:
    case eventType.VIDEO_PLAYBACK_COMPLETED:
    case eventType.VIDEO_QUARTILE_VOD: {
      const titleLowerCase = title?.toLowerCase();
      const isSpanish = titleLowerCase.includes('espanol') || titleLowerCase.includes('twce');
      const isLivePattrn = titleLowerCase.includes('pattrn');

      const content_library = helpers.getContentLibrary({ isANF, isSpanish, isLivePattrn, isLive, episode });
      const segment_display_name = helpers.getSegmentDisplayName({ anf, isANF, isLive, schedule, seriestitle });

      return {
        event,
        userId,
        properties: {
          ...globalEventProperties,
          ...(eventType.VIDEO_HEARTBEAT === event && { subscriptiontype }), // subscriptiontype only for VIDEO_HEARTBEAT
          asset_id: _id,
          episode_number: episode,
          epg_channel_number: isLive ? schedule.find(channel => channel.selected)?.channel_number : null,
          livestream: isLive ? 1 : 0,
          playback_session_id: `${_id}${timestamp}`,
          playlist_id: seasonID ?? null,
          session_id: sessionId,
          forecast_location: `${watchCity}, ${watchState}`,
          content_library,
          zip_code: defaultLocation?.zipCode,
          segment_display_name,
          episode_name: title,
          pin_my_home: defaultLocation?.city,
          cable_authenticated: isCableSubscriber ? 1 : 0,
          city: defaultLocation?.city,
          state: defaultLocation?.state,
          country: 'United States', // hardcoded for now
          lat: defaultLocation?.coords.lat,
          lon: defaultLocation?.coords.lng,
        },
      };
    }
    case eventType.ERROR_LOG: {
      const appPage = navigationService.getCurrentRoute();

      return {
        event,
        userId,
        properties: {
          appPage,
          source: apiErrorData?.source,
          payload: apiErrorData?.payload,
          responsecode: apiErrorData?.responseCode,
          response: apiErrorData?.response,
        },
      };
    }
    case eventType.VIDEO_ERROR_LOG: {
      const appPage = navigationService.getCurrentRoute();

      return {
        event,
        userId,
        properties: {
          type: appPage,
          source: videoErrorData?.source,
          exception: videoErrorData?.exception,
          responseCode: videoErrorData?.responseCode,
          response: videoErrorData?.response,
        },
      };
    }
    case eventType.DAILY_FORECAST:
    case eventType.HOURLY_FORECAST:
    case eventType.NOW_FORECAST: {
      return {
        event,
        userId,
        properties: {
          feature_id: weatherForecast?.featureId,
          mw_segment_id: weatherForecast?.mwSegmentId,
          sequence_count: weatherForecast?.weatherForecastCount,
          location_used: channelId,
        },
      };
    }
    case eventType.MAP_OVERLAY: {
      return {
        event,
        userId,
        properties: {
          feature_id: weatherMap?.featureId,
          mw_segment_id: weatherMap?.mwSegmentId,
          sequence_count: weatherMap?.weatherForecastCount,
          map_view: weatherMap?.mapView,
          location_used: channelId,
        },
      };
    }
    case eventType.MAP: {
      return {
        event,
        userId,
        properties: {
          feature_id: 'Map',
          mw_segment_id: 4,
          sequence_count: weatherMap?.weatherForecastCount,
          location_used: channelId,
        },
      };
    }
    case eventType.LOCATION_ADDED: {
      return {
        event,
        userId,
        properties: {
          current_default_location: currentLocation,
          channel_id: manageLocations?.nextLocation,
        },
      };
    }
    case eventType.LOCATION_SEARCHED: {
      return {
        event,
        userId,
        properties: {
          search_term: manageLocations?.searchTerm,
          current_default_location: currentLocation,
        },
      };
    }
    case eventType.DEFAULT_LOCATION: {
      return {
        event,
        userId,
        properties: {
          channel_id: manageLocations?.nextLocation,
          current_default_location: currentLocation,
        },
      };
    }
    case eventType.LIVE_RAMP_CALL: {
      return {
        event,
        userId,
        properties: liveRampData,
      };
    }
    case eventType.LIVE_RAMP_CALL_ERROR: {
      return {
        event,
        userId,
        properties: liveRampData,
      };
    }
    default:
      return null;
  }
};

const isVideoEvent = (event: Events) => {
  switch (event) {
    case eventType.VIDEO_HEARTBEAT:
    case eventType.VIDEO_PLAYBACK_STARTED:
    case eventType.VIDEO_PLAYBACK_COMPLETED:
    case eventType.VIDEO_QUARTILE_VOD:
      return true;
    default:
      return false;
  }
};

export const segmentTrackEvent = async (
  event: Events,
  userSettings: UserSettings & VideoData,
  arguments_: Arguments = {},
) => {
  const headers = getSegmentHeaders();
  const trackingData = getDataForEvent(event, userSettings, arguments_);

  if (isVideoEvent(event)) {
    const volume = await getDeviceVolume();

    if (trackingData) {
      trackingData.sound = volume;
    }
  }

  if (event && headers && event && trackingData) {
    return axios
      .post(apiConfig.SEGMENT_TRACK_URL, trackingData, { headers })
      .then(() => {
        console.log(`${event} event sent to Segment`);
      })
      .catch(error => {
        if (error.response) {
          error.response.data.success === false;
        }
        console.error(error);
      });
  }
};
